import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from '../design-system';
import {
  SmartButton as ExtractIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Fingerprint as FingerprintIcon,
} from '@mui/icons-material';
import FileUpload, { type UploadedFile } from './FileUpload';
import type { PendingTempFileAssociation } from './file-upload.types';
import SlotImageThumbnailGallery, {
  type SlotImageThumbItem,
} from '../containers/SlotImageThumbnailGallery';
import { TRACKED_UPLOAD_IMAGE_ACCEPT } from '@cnapse/shared/file-upload.constants.js';
import { fingerprintInferenceService } from '../../services/FingerprintInferenceService';
import { filesService } from '../../services/FilesService';
import {
  attachPreviewUrlsProgressive,
  buildFingerprintPlaceholders,
  loadFingerprintPreviewsProgressive,
  type FingerprintUploadedFile,
} from '../../utils/fingerprintPreviews';
import {
  waferHistoryToFingerprintMetadata,
} from '../../utils/waferImageHistory';
import {
  buildFingerprintInferenceBatchRequest,
  mergeServerAndPendingFingerprintFiles,
} from '../../utils/fingerprintInferenceBatch';
import {
  type FileMetadata,
  type WaferFingerprint,
  type FingerprintImageMetadata,
  type FingerprintInferenceAnalysisResult,
} from "@cnapse/shared/schemas.js";
import * as dt from '@cnapse/shared/data.types.js';

export interface FingerprintData {
  fingerprint?: WaferFingerprint;
  fingerprintImages?: FileMetadata[]; // Multiple images for robust OCR
  extractedFingerprint?: WaferFingerprint;
  pendingTempFileAssociations?: PendingTempFileAssociation[];
}

export interface FingerprintManagerProps {
  // Core data
  fingerprintData: FingerprintData;
  onFingerprintDataChange: (data: FingerprintData) => void;
  
  // Wafer context (for deletion and update operations)
  waferId?: dt.IntegerId;
  
  // Configuration
  showAIExtraction?: boolean;
  showImageSection?: boolean;
  showStringSection?: boolean;
  maxImages?: number; // Maximum total images allowed for upload (default: 10).
  
  /** When true, images and string are view-only (parent saves via header Save). */
  disabled?: boolean;
  /** Server-side uniqueness / validation error for the fingerprint string. */
  availabilityError?: string | null;
  /** View mode: fingerprint string from server (shown when disabled). */
  serverFingerprint?: WaferFingerprint | '';
}

interface FingerprintOcrAnalysisRow {
  id: string;
  imageName: string;
  ocrText: string;
  confidence: number | null;
  ocrFailed: boolean;
}

const OCR_RESOLUTION_METHOD_LABELS: Record<dt.OCR_RESOLUTION_METHOD, string> = {
  [dt.OCR_RESOLUTION_METHOD.StrongConsensus]: 'Strong consensus',
  [dt.OCR_RESOLUTION_METHOD.SimilarityAnalysis]: 'Similarity analysis',
  [dt.OCR_RESOLUTION_METHOD.CharacterVoting]: 'Character voting',
  [dt.OCR_RESOLUTION_METHOD.QualityWeighted]: 'Quality weighted',
  [dt.OCR_RESOLUTION_METHOD.SingleImage]: 'Single image',
  [dt.OCR_RESOLUTION_METHOD.AllRejected]: 'All rejected',
  [dt.OCR_RESOLUTION_METHOD.ManualEntry]: 'Manual entry',
  [dt.OCR_RESOLUTION_METHOD.UserOverride]: 'User override',
  [dt.OCR_RESOLUTION_METHOD.Error]: 'Error',
};

function basenameObjectKey(objectKey: string): string {
  const parts = objectKey.split('/');
  return parts[parts.length - 1] || objectKey;
}

function resolveFingerprintImageDisplayName(
  imageId: string,
  uploaded: UploadedFile[],
): string {
  const match = uploaded.find((file) => file.metadata?.objectKey === imageId);
  const name = match?.metadata?.originalName?.trim();
  return name && name.length > 0 ? name : basenameObjectKey(imageId);
}

function parseFingerprintWarning(warning: string): { objectKey?: string; message: string } {
  const colonIdx = warning.indexOf(': ');
  if (colonIdx === -1) {
    return { message: warning };
  }
  const prefix = warning.slice(0, colonIdx);
  if (prefix.includes('/')) {
    return { objectKey: prefix, message: warning.slice(colonIdx + 2) };
  }
  return { message: warning };
}

function confidenceChipColor(confidence: number): 'success' | 'warning' | 'error' {
  if (confidence >= 85) return 'success';
  if (confidence >= 70) return 'warning';
  return 'error';
}

export const FingerprintManager: React.FC<FingerprintManagerProps> = ({
  fingerprintData,
  onFingerprintDataChange,
  waferId,
  showImageSection = true,
  showStringSection = true,
  maxImages = 10,
  disabled = false,
  availabilityError = null,
  serverFingerprint = '',
}) => {
  const [uploadedFingerprintImage, setUploadedFingerprintImage] = useState<UploadedFile[]>([]);
  const [fingerprintPreviewsLoading, setFingerprintPreviewsLoading] = useState(false);
  const [fingerprintUploadKey, setFingerprintUploadKey] = useState(`fingerprint-${Date.now()}`);
  // OCR Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<FingerprintInferenceAnalysisResult | null>(null);
  const [highlightedGalleryObjectKey, setHighlightedGalleryObjectKey] = useState<string | null>(null);
  const [perImageBreakdownOpen, setPerImageBreakdownOpen] = useState(false);
  const [fingerprintCopied, setFingerprintCopied] = useState(false);
  // Local state for input to avoid re-renders on every keystroke
  const [localFingerprintValue, setLocalFingerprintValue] = useState<string>('');

  const fingerprintDataRef = useRef(fingerprintData);
  fingerprintDataRef.current = fingerprintData;
  const onFingerprintDataChangeRef = useRef(onFingerprintDataChange);
  onFingerprintDataChangeRef.current = onFingerprintDataChange;
  const uploadedFingerprintImageRef = useRef(uploadedFingerprintImage);
  uploadedFingerprintImageRef.current = uploadedFingerprintImage;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  /** Bumped on wafer switch so stale image-history / presign responses are ignored. */
  const previewSyncGenerationRef = useRef(0);

  // View: server string. Edit: parent draft.
  useEffect(() => {
    setLocalFingerprintValue(
      disabled ? serverFingerprint || '' : fingerprintData.fingerprint || '',
    );
  }, [disabled, serverFingerprint, fingerprintData.fingerprint, waferId]);

  const mergePreviewFile = useCallback((file: FingerprintUploadedFile) => {
    setUploadedFingerprintImage((prev) => {
      const objectKey = file.metadata.objectKey;
      if (!objectKey || !prev.some((p) => p.metadata.objectKey === objectKey)) {
        return prev;
      }
      return prev.map((p) => (p.metadata.objectKey === objectKey ? file : p));
    });
  }, []);

  const startProgressivePreviews = useCallback(
    (images: FingerprintImageMetadata[], isCancelled: () => boolean) => {
      setUploadedFingerprintImage(buildFingerprintPlaceholders(images));
      loadFingerprintPreviewsProgressive(images, {
        isCancelled,
        onFileReady: mergePreviewFile,
      });
    },
    [mergePreviewFile],
  );

  const syncFingerprintImagesFromServer = useCallback(
    async (options?: {
      showLoading?: boolean;
      isStale?: () => boolean;
      /** When false (wafer switch), do not carry another wafer's local uploads forward. */
      preservePendingUploads?: boolean;
    }): Promise<FingerprintImageMetadata[]> => {
      if (!waferId) {
        return (fingerprintDataRef.current.fingerprintImages ?? []) as FingerprintImageMetadata[];
      }

      const isStale = options?.isStale ?? (() => false);
      const preservePendingUploads = options?.preservePendingUploads ?? true;
      const syncGeneration = previewSyncGenerationRef.current;
      const showLoading = options?.showLoading ?? false;
      if (showLoading && !isStale()) {
        setFingerprintPreviewsLoading(true);
      }

      let imagesForState: FingerprintImageMetadata[] = [];

      try {
        const historyData = await filesService.getWaferImageHistory(waferId);
        if (isStale() || syncGeneration !== previewSyncGenerationRef.current) {
          return [];
        }
        imagesForState = (historyData.images ?? []).map(waferHistoryToFingerprintMetadata);
        if (!disabledRef.current) {
          onFingerprintDataChangeRef.current({
            ...fingerprintDataRef.current,
            fingerprintImages: imagesForState,
          });
        }
      } catch (error) {
        console.error('Failed to load wafer fingerprint images from history:', error);
        imagesForState = [];
      } finally {
        if (showLoading && !isStale()) {
          setFingerprintPreviewsLoading(false);
        }
      }

      if (isStale() || syncGeneration !== previewSyncGenerationRef.current) {
        return [];
      }

      if (imagesForState.length === 0) {
        // Unsaved local/temp uploads are not in image-history yet — keep them visible.
        if (!preservePendingUploads || uploadedFingerprintImageRef.current.length === 0) {
          setUploadedFingerprintImage([]);
          uploadedFingerprintImageRef.current = [];
        }
        return [];
      }

      const serverKeys = new Set(imagesForState.map((img) => img.objectKey));
      const pendingKept = preservePendingUploads
        ? uploadedFingerprintImageRef.current.filter((file) => {
            if (file.metadata?.fromTempFile) {
              return true;
            }
            const key = file.metadata?.objectKey;
            return Boolean(key && !serverKeys.has(key));
          })
        : [];

      const nextFiles = [
        ...buildFingerprintPlaceholders(imagesForState),
        ...pendingKept,
      ];
      uploadedFingerprintImageRef.current = nextFiles;
      setUploadedFingerprintImage(nextFiles);
      loadFingerprintPreviewsProgressive(imagesForState, {
        isCancelled: () =>
          isStale() || syncGeneration !== previewSyncGenerationRef.current,
        onFileReady: mergePreviewFile,
      });
      return imagesForState;
    },
    [waferId, mergePreviewFile],
  );

  // Existing wafer: one image-history fetch + one presigned batch per open (not duplicated in parent).
  useEffect(() => {
    if (!waferId) {
      return;
    }

    const syncGeneration = ++previewSyncGenerationRef.current;
    uploadedFingerprintImageRef.current = [];
    setUploadedFingerprintImage([]);
    setAnalysisResults(null);
    setPerImageBreakdownOpen(false);
    setHighlightedGalleryObjectKey(null);
    setFingerprintUploadKey(`fingerprint-wafer-${waferId}-${syncGeneration}`);

    let cancelled = false;
    const isStale = (): boolean =>
      cancelled || syncGeneration !== previewSyncGenerationRef.current;

    const loadPreviews = async (): Promise<void> => {
      if (!isStale()) {
        setFingerprintPreviewsLoading(true);
      }
      await syncFingerprintImagesFromServer({
        showLoading: false,
        isStale,
        preservePendingUploads: false,
      });
      if (!isStale()) {
        setFingerprintPreviewsLoading(false);
      }
    };

    void loadPreviews();

    return () => {
      cancelled = true;
    };
  }, [waferId, syncFingerprintImagesFromServer]);

  // Optimized handler to update fingerprint field with debouncing
  const handleFingerprintChange = useCallback((value: string) => {
    setLocalFingerprintValue(value);

    const trimmedValue = value.trim();
    const newFingerprint = trimmedValue === '' ? undefined : (value as WaferFingerprint);
    const current = fingerprintDataRef.current;

    if (current.fingerprint !== newFingerprint) {
      onFingerprintDataChangeRef.current({
        ...current,
        fingerprint: newFingerprint,
      });
    }
  }, []);

  // New wafer / no waferId: load previews from props only (existing wafer uses waferId effect above).
  useEffect(() => {
    if (waferId) {
      return;
    }

    let cancelled = false;

    const loadExistingImages = async (): Promise<void> => {
      if (!fingerprintData.fingerprintImages?.length) {
        if (!cancelled) {
          setUploadedFingerprintImage([]);
        }
        return;
      }

      if (!cancelled) {
        startProgressivePreviews(
          (fingerprintData.fingerprintImages ?? []) as FingerprintImageMetadata[],
          () => cancelled,
        );
      }
    };

    void loadExistingImages();

    return () => {
      cancelled = true;
    };
  }, [waferId, fingerprintData.fingerprintImages, startProgressivePreviews]);

  const handleFileChange = async (files: UploadedFile[]) => {
    const previousImageCount = uploadedFingerprintImage.length;
    const currentImageCount = files.length;
    
    // Reset analysis results when files change
    setAnalysisResults(null);
    
    // Handle deletion case (when all files are removed); persisted deletes run on parent Save.
    if (previousImageCount > 0 && currentImageCount === 0) {
      uploadedFingerprintImageRef.current = [];
      setUploadedFingerprintImage([]);
      onFingerprintDataChangeRef.current({
        ...fingerprintDataRef.current,
        fingerprintImages: [],
        pendingTempFileAssociations: [],
      });
      setFingerprintUploadKey(`cleared-${Date.now()}`);
      return;
    }

    // After device upload, reload from server so gallery/count match DB (avoids partial-batch drift).
    if (waferId && currentImageCount > previousImageCount) {
      const newlyAdded = files.slice(previousImageCount);
      if (!newlyAdded.some((f) => f.metadata.fromTempFile)) {
        await syncFingerprintImagesFromServer({ showLoading: true });
        return;
      }
    }

    uploadedFingerprintImageRef.current = files;
    setUploadedFingerprintImage(files);

    if (files.length > 0) {
      const imageMetadataArray = files.map((file) => file.metadata);
      onFingerprintDataChangeRef.current({
        ...fingerprintDataRef.current,
        fingerprintImages: imageMetadataArray,
      });
      attachPreviewUrlsProgressive(files as FingerprintUploadedFile[], {
        onFileReady: mergePreviewFile,
      });
    }
  };

  // Remove one image from local state; persisted deletes run on parent Save.
  const handleDeleteImage = (index: number) => {
    const removed = uploadedFingerprintImage[index];
    const removedFileId = (removed?.metadata as { fileId?: number }).fileId;
    const updatedFiles = uploadedFingerprintImage.filter((_, i) => i !== index);
    const remainingMetadata = updatedFiles.map((file) => file.metadata);
    uploadedFingerprintImageRef.current = updatedFiles;
    const current = fingerprintDataRef.current;
    onFingerprintDataChangeRef.current({
      ...current,
      fingerprintImages: remainingMetadata,
      ...(removedFileId != null
        ? {
            pendingTempFileAssociations: (
              current.pendingTempFileAssociations ?? []
            ).filter((p) => p.fileId !== removedFileId),
          }
        : {}),
    });
    setUploadedFingerprintImage(updatedFiles);
    setAnalysisResults(null);
  };

  // Handler to analyze fingerprint images
  const handleAnalyzeImages = async () => {
    if (uploadedFingerprintImage.length === 0) {
      console.error('No images to analyze');
      return;
    }

    setAnalyzing(true);
    setAnalysisResults(null);
    setPerImageBreakdownOpen(false);
    setHighlightedGalleryObjectKey(null);

    try {
      const localFiles = uploadedFingerprintImageRef.current;
      let filesForAnalysis = localFiles;

      if (waferId) {
        const historyData = await filesService.getWaferImageHistory(waferId);
        const serverImages = (historyData.images ?? []).map(waferHistoryToFingerprintMetadata);
        filesForAnalysis = mergeServerAndPendingFingerprintFiles(serverImages, localFiles);
      }

      const response = await fingerprintInferenceService.runFingerprintInferenceAnalysis(
        buildFingerprintInferenceBatchRequest(
          filesForAnalysis,
          waferId
            ? dt.FINGERPRINT_INFERENCE_BATCH_CONTEXT.WAFER_REVIEW
            : dt.FINGERPRINT_INFERENCE_BATCH_CONTEXT.MANUAL_DIAGNOSTIC,
          { waferId },
        ),
      );

      setAnalysisResults(response.data);
    } catch (error) {
      console.error('Failed to analyze images:', error);
      setAnalysisResults({
        ocrResults: [],
        allImagesLowQuality: false,
        warnings: ['Analysis failed: ' + (error instanceof Error ? error.message : 'Unknown error')],
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const activeFingerprintCount = uploadedFingerprintImage.length;
  const remainingUploadSlots = Math.max(0, maxImages - activeFingerprintCount);

  const fingerprintGalleryItems = useMemo((): SlotImageThumbItem[] => {
    return uploadedFingerprintImage
      .filter((img) => img.metadata?.objectKey)
      .map((img) => {
        const objectKey = img.metadata!.objectKey!;
        const metadata = img.metadata as FingerprintImageMetadata;
        const ocrConfidence = metadata.ocrConfidence;
        return {
          objectKey,
          fileId: metadata.fileId,
          downloadFilename: img.metadata?.originalName,
          primaryCaption: img.metadata?.originalName ?? undefined,
          secondaryCaption:
            ocrConfidence !== undefined ? `OCR ${ocrConfidence.toFixed(0)}%` : undefined,
          confidence: ocrConfidence !== undefined ? ocrConfidence / 100 : null,
        };
      });
  }, [uploadedFingerprintImage]);

  const { ocrAnalysisRows, ocrAnalysisGlobalWarnings } = useMemo(() => {
    if (!analysisResults) {
      return { ocrAnalysisRows: [] as FingerprintOcrAnalysisRow[], ocrAnalysisGlobalWarnings: [] as string[] };
    }

    const ocrByImageId = new Map(
      analysisResults.ocrResults.map((result) => [result.imageId, result] as const),
    );
    const warningByObjectKey = new Map<string, string>();
    const globalWarnings: string[] = [];

    for (const warning of analysisResults.warnings ?? []) {
      const parsed = parseFingerprintWarning(warning);
      if (parsed.objectKey) {
        warningByObjectKey.set(parsed.objectKey, parsed.message);
      } else {
        globalWarnings.push(parsed.message);
      }
    }

    const imageIds = new Set<string>();
    for (const file of uploadedFingerprintImage) {
      const objectKey = file.metadata?.objectKey;
      if (objectKey) {
        imageIds.add(objectKey);
      }
    }
    for (const result of analysisResults.ocrResults) {
      imageIds.add(result.imageId);
    }
    for (const objectKey of warningByObjectKey.keys()) {
      imageIds.add(objectKey);
    }

    const rows: FingerprintOcrAnalysisRow[] = Array.from(imageIds).map((imageId, index) => {
      const ocr = ocrByImageId.get(imageId);
      const hasImageError = warningByObjectKey.has(imageId);
      return {
        id: imageId || String(index),
        imageName: resolveFingerprintImageDisplayName(imageId, uploadedFingerprintImage),
        ocrText: ocr?.text?.trim() || '',
        confidence: ocr?.confidence ?? null,
        ocrFailed: hasImageError,
      };
    });

    if (analysisResults.allImagesLowQuality && rows.length > 0) {
      globalWarnings.push('All images are below the quality threshold.');
    }

    return { ocrAnalysisRows: rows, ocrAnalysisGlobalWarnings: globalWarnings };
  }, [analysisResults, uploadedFingerprintImage]);

  const handleGalleryRemove = (objectKey: string) => {
    const index = uploadedFingerprintImage.findIndex(
      (img) => img.metadata?.objectKey === objectKey,
    );
    if (index >= 0) {
      handleDeleteImage(index);
    }
    if (highlightedGalleryObjectKey === objectKey) {
      setHighlightedGalleryObjectKey(null);
    }
  };

  const resolvedFingerprintValue = useMemo(() => {
    const fromResolution = analysisResults?.resolution?.finalValue?.trim();
    if (fromResolution) {
      return fromResolution;
    }
    if (analysisResults?.ocrResults.length === 1) {
      return analysisResults.ocrResults[0].text?.trim() ?? '';
    }
    return '';
  }, [analysisResults]);

  const applyResolvedFingerprint = () => {
    if (resolvedFingerprintValue) {
      handleFingerprintChange(resolvedFingerprintValue);
    }
  };

  const extractionMetaLine = useMemo(() => {
    if (!analysisResults) {
      return '';
    }
    if (analysisResults.resolution) {
      const method =
        OCR_RESOLUTION_METHOD_LABELS[analysisResults.resolution.resolutionMethod] ??
        analysisResults.resolution.resolutionMethod;
      return `${Math.round(analysisResults.resolution.finalConfidence)}% confidence · ${method}`;
    }
    return 'Unresolved';
  }, [analysisResults]);

  const fingerprintAlreadyApplied =
    resolvedFingerprintValue.length > 0 &&
    localFingerprintValue.trim() === resolvedFingerprintValue.trim();

  const showPerImageBreakdown = ocrAnalysisRows.length > 1;

  const displayFingerprintValue = disabled
    ? (serverFingerprint || '').trim()
    : localFingerprintValue.trim();

  const handleCopyFingerprint = useCallback(async () => {
    if (!displayFingerprintValue) {
      return;
    }
    try {
      await navigator.clipboard.writeText(displayFingerprintValue);
      setFingerprintCopied(true);
      window.setTimeout(() => setFingerprintCopied(false), 2000);
    } catch {
      /* clipboard denied */
    }
  }, [displayFingerprintValue]);

  useEffect(() => {
    setPerImageBreakdownOpen(false);
  }, [analysisResults]);

  useEffect(() => {
    if (!highlightedGalleryObjectKey) {
      return;
    }
    const stillPresent = fingerprintGalleryItems.some(
      (item) => item.objectKey === highlightedGalleryObjectKey,
    );
    if (!stillPresent) {
      setHighlightedGalleryObjectKey(null);
    }
  }, [fingerprintGalleryItems, highlightedGalleryObjectKey]);

  return (
    <Stack spacing={2.5}>
      {showImageSection && (
        <Box>
          <Stack spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Images
              </Typography>
              {!disabled ? (
                <FileUpload
                  key={fingerprintUploadKey}
                  files={uploadedFingerprintImage}
                  onFilesChange={handleFileChange}
                  type="images"
                  accept={TRACKED_UPLOAD_IMAGE_ACCEPT}
                  maxFiles={maxImages}
                  disabled={disabled || remainingUploadSlots === 0}
                  compact
                  compactHideHint
                  showFileList={false}
                  showPreview={false}
                  entityType="wafer"
                  entityId={waferId}
                  fileCategory="fingerprint_image"
                  allowTempFilePick
                  pendingTempFileAssociations={fingerprintData.pendingTempFileAssociations ?? []}
                  onPendingTempFileAssociationsChange={(pendingTempFileAssociations) =>
                    onFingerprintDataChangeRef.current({
                      ...fingerprintDataRef.current,
                      pendingTempFileAssociations,
                      // FileUpload fires this after onFilesChange; re-apply images from the
                      // synced ref so a stale fingerprintData closure cannot drop temp imports.
                      fingerprintImages: uploadedFingerprintImageRef.current.map(
                        (file) => file.metadata,
                      ),
                    })
                  }
                />
              ) : null}
            </Stack>
            {fingerprintPreviewsLoading ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Loading fingerprint images...
              </Typography>
            ) : (
              <SlotImageThumbnailGallery
                items={fingerprintGalleryItems}
                onRemove={disabled ? undefined : handleGalleryRemove}
                emptyMessage="No fingerprint images."
                thumbSize={150}
                centered
                highlightedObjectKey={highlightedGalleryObjectKey}
                onItemActivate={setHighlightedGalleryObjectKey}
              />
            )}


            {!disabled && uploadedFingerprintImage.length > 0 ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <AppButton
                    variant="contained"
                    color="primary"
                    onClick={handleAnalyzeImages}
                    disabled={analyzing || uploadedFingerprintImage.length === 0}
                    startIcon={analyzing ? undefined : <ExtractIcon />}
                  >
                    {analyzing ? 'Extracting...' : 'Extract Fingerprint'}
                  </AppButton>
                </Box>

                {analysisResults ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                    <Stack spacing={1.5}>
                      {ocrAnalysisGlobalWarnings.length > 0 ? (
                        <Alert severity="warning" sx={{ py: 0 }}>
                          {ocrAnalysisGlobalWarnings.join(' ')}
                        </Alert>
                      ) : null}

                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          Extraction result
                        </Typography>
                        {analysisResults.resolution ? (
                          <Chip
                            label={`${Math.round(analysisResults.resolution.finalConfidence)}%`}
                            size="small"
                            color={confidenceChipColor(
                              analysisResults.resolution.finalConfidence,
                            )}
                            sx={{ height: 22 }}
                          />
                        ) : (
                          <Chip label="Unresolved" size="small" variant="outlined" sx={{ height: 22 }} />
                        )}
                      </Stack>

                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          wordBreak: 'break-all',
                          color: resolvedFingerprintValue ? 'text.primary' : 'text.secondary',
                        }}
                      >
                        {resolvedFingerprintValue || '(no text extracted)'}
                      </Typography>

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {extractionMetaLine}
                          </Typography>
                          {analysisResults.resolution?.explanation ? (
                            <Typography variant="caption" color="text.secondary">
                              {analysisResults.resolution.explanation}
                            </Typography>
                          ) : null}
                          {analysisResults.resolution?.requiredUserReview ? (
                            <Chip
                              icon={<WarningIcon />}
                              label="Review required"
                              size="small"
                              color="warning"
                              sx={{ alignSelf: 'flex-start', height: 22, mt: 0.5 }}
                            />
                          ) : null}
                        </Stack>
                        {resolvedFingerprintValue ? (
                          <AppButton
                            size="small"
                            variant="outlined"
                            onClick={applyResolvedFingerprint}
                            disabled={fingerprintAlreadyApplied}
                            sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                          >
                            {fingerprintAlreadyApplied ? 'Applied' : 'Apply to fingerprint'}
                          </AppButton>
                        ) : null}
                      </Stack>

                      {showPerImageBreakdown ? (
                        <Accordion
                          disableGutters
                          elevation={0}
                          expanded={perImageBreakdownOpen}
                          onChange={(_, expanded) => setPerImageBreakdownOpen(expanded)}
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            '&:before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                            <Typography variant="body2" color="text.secondary">
                              Per-image breakdown ({ocrAnalysisRows.length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1 }}>
                            <Stack divider={<Divider />} spacing={0}>
                              {ocrAnalysisRows.map((row) => {
                                const isActive = highlightedGalleryObjectKey === row.id;
                                return (
                                  <Box
                                    key={row.id}
                                    onClick={() => setHighlightedGalleryObjectKey(row.id)}
                                    sx={{
                                      py: 1,
                                      px: 0.5,
                                      cursor: 'pointer',
                                      borderRadius: 0.5,
                                      bgcolor: isActive ? 'action.selected' : 'transparent',
                                      '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1.5}
                                      alignItems="center"
                                      sx={{ minWidth: 0 }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                        title={row.imageName}
                                        sx={{ width: '28%', flexShrink: 0 }}
                                      >
                                        {row.imageName}
                                      </Typography>
                                      {row.ocrFailed ? (
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          alignItems="center"
                                          sx={{ flex: 1, minWidth: 0 }}
                                        >
                                          <WarningIcon fontSize="small" color="warning" />
                                          <Typography variant="body2" color="warning.main">
                                            Failed
                                          </Typography>
                                        </Stack>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            fontFamily: row.ocrText ? 'monospace' : undefined,
                                            wordBreak: 'break-word',
                                          }}
                                          title={row.ocrText || undefined}
                                        >
                                          {row.ocrText || '(no text)'}
                                        </Typography>
                                      )}
                                      {row.confidence !== null && !row.ocrFailed ? (
                                        <Chip
                                          label={`${Math.round(row.confidence)}%`}
                                          size="small"
                                          color={confidenceChipColor(Math.round(row.confidence))}
                                          sx={{ height: 22, flexShrink: 0 }}
                                        />
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ width: 48, textAlign: 'right', flexShrink: 0 }}
                                        >
                                          {'\u2014'}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : null}
              </>
            ) : null}

          </Stack>
        </Box>
      )}

      {showStringSection && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <FingerprintIcon
              fontSize="small"
              color="action"
              aria-hidden
              sx={{ flexShrink: 0, alignSelf: 'center' }}
            />
            {disabled ? (
              <>
                {displayFingerprintValue ? (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      py: 0.875,
                      px: 1.5,
                      borderRadius: '16px',
                      bgcolor: 'action.hover',
                      color: 'text.primary',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.43,
                      wordBreak: 'break-all',
                    }}
                  >
                    {displayFingerprintValue}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 0.5,
                      px: 1.25,
                      borderRadius: '16px',
                      border: 1,
                      borderStyle: 'dashed',
                      borderColor: 'divider',
                      color: 'text.secondary',
                      fontSize: '0.8125rem',
                      lineHeight: 1.43,
                    }}
                  >
                    Not set
                  </Box>
                )}
                {displayFingerprintValue ? (
                  <AppIconButton
                    onClick={() => void handleCopyFingerprint()}
                    title={fingerprintCopied ? 'Copied' : 'Copy fingerprint'}
                    aria-label="Copy fingerprint"
                    sx={{ flexShrink: 0 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </AppIconButton>
                ) : null}
              </>
            ) : (
              <AppTextField
                fullWidth
                size="small"
                value={localFingerprintValue}
                onChange={(e) => handleFingerprintChange(e.target.value)}
                placeholder="Enter manually or apply from extraction above"
                error={Boolean(availabilityError)}
                helperText={availabilityError ?? undefined}
                sx={{ flex: 1, minWidth: 0 }}
                slotProps={{
                  input: {
                    sx: { fontFamily: 'monospace' },
                    'aria-label': 'Fingerprint',
                  },
                }}
              />
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default FingerprintManager;
